import React from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
  Skeleton,
  Checkbox,
  Tooltip,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import { theme } from "../../../../Theme";
import { PROMO_COLORS } from "../../constants/colors";
import { EMPTY_WEAK_SKU_RANKINGS } from "../../services/promoSimulatorService";
import { CollapsibleSection } from "./CollapsibleSection";
import { EmptyState } from "./EmptyState";
import { fmtX } from "./helpers";

const WEAK_SKU_RANKING_LIST_MAX_HEIGHT = 320;

function fmtTrendPctShort(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  const formatted = n.toLocaleString("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });
  return n > 0 ? `+${formatted}%` : `${formatted}%`;
}

function RankingPanel({
  title,
  subtitle,
  accentColor,
  icon,
  rows,
  loading,
  emptyMessage,
  renderMeta,
  selectedSkus,
  onToggleSku,
}) {
  const allRowSkus = rows.map((r) => r.sku);
  const allSelected =
    allRowSkus.length > 0 && allRowSkus.every((s) => selectedSkus.has(s));
  const someSelected = allRowSkus.some((s) => selectedSkus.has(s));

  const handleSelectAll = () => {
    if (allSelected) {
      allRowSkus.forEach((s) => onToggleSku(s, false));
    } else {
      allRowSkus.forEach((s) => onToggleSku(s, true));
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid #E8ECF0",
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 2.25,
          py: 1.75,
          borderBottom: "1px solid #EEF1F4",
          bgcolor: "#FAFBFC",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${accentColor}14`,
              color: accentColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              color="text.primary"
              sx={{ letterSpacing: "-0.01em" }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11, lineHeight: 1.45, display: "block" }}
            >
              {subtitle}
            </Typography>
          </Box>
          {rows.length > 0 && !loading && (
            <Tooltip title={allSelected ? "Deselect all" : "Select all"} arrow>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={handleSelectAll}
                sx={{
                  p: 0.5,
                  color: "#CBD5E1",
                  "&.Mui-checked": { color: accentColor },
                  "&.MuiCheckbox-indeterminate": { color: accentColor },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          maxHeight: WEAK_SKU_RANKING_LIST_MAX_HEIGHT,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {loading ? (
          <Stack spacing={1.25}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={52} />
            ))}
          </Stack>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              py: 3,
              px: 1.5,
              textAlign: "center",
              borderRadius: 1.5,
              border: "1px dashed #E2E8F0",
              bgcolor: "#FAFBFC",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0}>
            {rows.map((row, idx) => {
              const isChecked = selectedSkus.has(row.sku);
              return (
                <Box
                  key={row.sku}
                  onClick={() => onToggleSku(row.sku, !isChecked)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    py: 1.1,
                    px: 1,
                    mx: -1,
                    borderRadius: 1.25,
                    borderBottom:
                      idx < rows.length - 1 ? "1px solid #F1F5F9" : "none",
                    cursor: "pointer",
                    transition: "background 0.12s",
                    bgcolor: isChecked ? `${accentColor}0c` : "transparent",
                    "&:hover": {
                      bgcolor: isChecked ? `${accentColor}16` : "#F8FAFC",
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSku(row.sku, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      p: 0.25,
                      color: "#CBD5E1",
                      "&.Mui-checked": { color: accentColor },
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      width: 24,
                      minWidth: 24,
                      height: 24,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 800,
                      color: idx === 0 ? "#fff" : "text.secondary",
                      bgcolor: idx === 0 ? accentColor : "#EEF1F4",
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="text.primary"
                      noWrap
                      title={row.sku}
                    >
                      {row.sku}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 11 }}
                    >
                      {renderMeta(row)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

export function WeakSKUDetectionSection({
  rankings,
  loading,
  error,
  selectedSkus,
  onToggleSku,
  filterBar,
}) {
  const { declining, lowestRoi, highestPromoDep, highestCannibal } =
    rankings ?? EMPTY_WEAK_SKU_RANKINGS;

  const hasPanelData =
    declining.length > 0 ||
    lowestRoi.length > 0 ||
    highestPromoDep.length > 0 ||
    highestCannibal.length > 0;

  return (
    <CollapsibleSection
      sx={{ overflow: "hidden" }}
      icon={<WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />}
      title="Weak SKU Detection"
      subtitle="Pre-ranked weak SKU lists from the API for the current filters — select SKUs to send to Simulator or Optimizer"
      filterBar={filterBar}
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} key={`weak-sku-skel-${i}`}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
          ))}
        </Grid>
      ) : !hasPanelData ? (
        <EmptyState message="No weak SKU rankings for the selected filters. Adjust brand, year, or week range and try again." />
      ) : (
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <RankingPanel
              title="Top declining SKUs"
              subtitle="By volume trend % (from API)"
              accentColor={PROMO_COLORS.threat}
              icon={<TrendingDownIcon sx={{ fontSize: 20 }} />}
              rows={declining}
              loading={false}
              emptyMessage="No declining SKUs returned for this filter."
              selectedSkus={selectedSkus}
              onToggleSku={onToggleSku}
              renderMeta={(row) =>
                `${row.volumeTrendStatus} vol · ${fmtX(row.avgRoi)} ROI`
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RankingPanel
              title="Lowest ROI SKUs"
              subtitle="Lowest actual ROI (from API)"
              accentColor="#7C3AED"
              icon={<AssessmentOutlinedIcon sx={{ fontSize: 20 }} />}
              rows={lowestRoi}
              loading={false}
              emptyMessage="No low-ROI SKUs returned for this filter."
              selectedSkus={selectedSkus}
              onToggleSku={onToggleSku}
              renderMeta={(row) =>
                `${fmtX(row.avgRoi)} ROI · ${
                  row.promoDependencyPct != null
                    ? `${row.promoDependencyPct}% promo dep.`
                    : "— promo dep."
                }`
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RankingPanel
              title="Highest promo dependency"
              subtitle="Highest promo dependency % (from API)"
              accentColor={theme.palette.primary.main}
              icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
              rows={highestPromoDep}
              loading={false}
              emptyMessage="No promo-dependent SKUs returned for this filter."
              selectedSkus={selectedSkus}
              onToggleSku={onToggleSku}
              renderMeta={(row) =>
                `${
                  row.promoDependencyPct != null ? `${row.promoDependencyPct}%` : "—"
                } promo dep. · ${fmtX(row.avgRoi)} ROI`
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RankingPanel
              title="Highest cannibalization risk"
              subtitle="By cannibalization risk score (from API)"
              accentColor={PROMO_COLORS.warning}
              icon={<CallSplitOutlinedIcon sx={{ fontSize: 20 }} />}
              rows={highestCannibal}
              loading={false}
              emptyMessage="No cannibalization risk SKUs returned for this filter."
              selectedSkus={selectedSkus}
              onToggleSku={onToggleSku}
              renderMeta={(row) => {
                const partner = row.topCannibalizationPartner;
                const partnerBit = partner ? ` · vs ${partner}` : "";
                return `${row.cannibalizationRisk} risk (${
                  row.cannibalizationRiskScore != null
                    ? Number(row.cannibalizationRiskScore).toFixed(1)
                    : "—"
                })${partnerBit}`;
              }}
            />
          </Grid>
        </Grid>
      )}
    </CollapsibleSection>
  );
}
